import ArrowBack from '@mui/icons-material/ArrowBack'
import ArrowForward from '@mui/icons-material/ArrowForward'
import Download from '@mui/icons-material/Download'
import Print from '@mui/icons-material/Print'
import PrintOutlined from '@mui/icons-material/PrintOutlined'
import Save from '@mui/icons-material/Save'
import Settings from '@mui/icons-material/Settings'
import { Spinner } from 'react-bootstrap'
import { PRESCRIPTION_BUILDER_VIEW_TYPES } from 'src/consumer/constants'
import { PMPButtonForConsultation, PMPButtonsContainer } from '../PrescriptionMakingPageStyles'
import Tooltip from '@mui/material/Tooltip'
import ExportTypeSelector from 'src/clinic/views/clinicBrandPayoutReports/components/ExportTypeSelector'
import { EXPORT_TYPES } from 'src/clinic/views/clinicBrandPayoutReports/util'

// Modernised section-navigation buttons for the tablet layout header bar.
const NAV_BUTTON_BASE_SX = {
  pointerEvents: 'all',
  px: 2,
  py: 1,
  borderRadius: '12px',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  gap: '6px',
  maxWidth: '40%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
}
// Previous = quiet/ghost (secondary action)
const PREV_NAV_BUTTON_SX = {
  ...NAV_BUTTON_BASE_SX,
  color: '#4338ca',
  background: '#ffffff',
  border: '1px solid rgba(67, 56, 202, 0.3)',
  '&:hover': {
    background: 'rgba(67, 56, 202, 0.06)',
    borderColor: 'rgba(67, 56, 202, 0.5)',
    transform: 'translateY(-1px)',
  },
}
// Next = prominent/filled (primary forward action)
const NEXT_NAV_BUTTON_SX = {
  ...NAV_BUTTON_BASE_SX,
  color: '#ffffff',
  background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
  border: '1px solid transparent',
  boxShadow: '0 2px 8px rgba(67, 56, 202, 0.25)',
  '&:hover': {
    background: 'linear-gradient(135deg, #3730a3 0%, #4f46e5 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(67, 56, 202, 0.35)',
  },
}

// Single gear button — opens the Edit Sections drawer directly (the Editor Layout
// switch lives inline inside that drawer). Kept self-contained so it can be dropped
// into every layout's action stack, guaranteeing it is always reachable.
function SettingsMenuButton({ onEditSections }) {
  if (!onEditSections) {
    return null
  }
  return (
    <PMPButtonForConsultation
      whitebg
      onClick={onEditSections}
      style={{ pointerEvents: 'all', width: 'min-content' }}
    >
      <Tooltip title="Edit Sections & Layout" placement="left">
        <Settings />
      </Tooltip>
    </PMPButtonForConsultation>
  )
}

export default function TopPatientDetailsHeading({
  saveAndPrint,
  prescriptionSaveLoading,
  savePrescriptionData,
  prescriptionViewSettings,
  currentPage,
  emrSectionsToShowInOrder,
  showPatientDetails,
  nextSection,
  previousSection,
  currentPageHistory,
  inChamber = false,
  saveAndDownloadPDF,
  onRxPrint, // Callback to open Rx drawer and trigger PRINT ALL
  onQuickPrint, // Callback to trigger Quick Print
  onEditSections, // Callback to open EMR sections editor dialog (Editor Layout lives inside it)
}) {
  function renderRightHandSideButtonsForTabletView() {
    if (currentPage < emrSectionsToShowInOrder.length - 2) {
      // Mid-flow pages of the tablet layout only show "Next" — but the Settings gear
      // must stay reachable here too, otherwise there is no way to switch layout back
      // out of tablet view until the very last page.
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PMPButtonForConsultation sx={NEXT_NAV_BUTTON_SX} onClick={nextSection}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {emrSectionsToShowInOrder?.[currentPage + 2]?.title}
            </span>
            <ArrowForward sx={{ fontSize: '1.1rem' }} />
          </PMPButtonForConsultation>
          <SettingsMenuButton onEditSections={onEditSections} />
        </div>
      )
    }
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          gap: '8px',
          justifyContent: 'flex-end',
        }}
      >
        <PMPButtonForConsultation
          style={{ pointerEvents: 'all', width: 'min-content' }}
          whitebg
          onClick={saveAndPrint}
        >
          <Tooltip title="Save and Print" placement="left">
            <PrintOutlined />
          </Tooltip>
        </PMPButtonForConsultation>
        {onQuickPrint && (
          <PMPButtonForConsultation
            style={{ pointerEvents: 'all', width: 'min-content' }}
            whitebg
            onClick={onQuickPrint}
          >
            <Tooltip title="Quick Print" placement="left">
              <Print />
            </Tooltip>
          </PMPButtonForConsultation>
        )}
        <PMPButtonForConsultation
          style={{ pointerEvents: 'all', width: 'min-content' }}
          disabled={prescriptionSaveLoading}
          onClick={savePrescriptionData}
        >
          <Tooltip title="Save Only" placement="left">
            <Save />
          </Tooltip>
          {prescriptionSaveLoading && <Spinner animation="border" variant="light" size="sm" />}
        </PMPButtonForConsultation>
        <SettingsMenuButton onEditSections={onEditSections} />
      </div>
    )
  }

  function handleDownloadPDF(exportType) {
    switch (exportType) {
      case EXPORT_TYPES.pdf.id:
        saveAndDownloadPDF({ sendImmediately: false })
        break
      case EXPORT_TYPES.email_pdf.id:
        saveAndDownloadPDF({ sendImmediately: true })
        break
      default:
        break
    }
  }

  const isTabletView =
    prescriptionViewSettings?.prescriptionBuilderViewType ===
    PRESCRIPTION_BUILDER_VIEW_TYPES.tabletView

  // Titles of the section(s) currently on screen — shown centred in the tablet header bar
  // so the practitioner always knows where they are in the flow.
  const visibleSectionTitles = [
    emrSectionsToShowInOrder?.[currentPage]?.title,
    emrSectionsToShowInOrder?.[currentPage + 1]?.title,
  ]
    .filter(Boolean)
    .join('  ·  ')

  // Tablet view: a real, full-width header bar that sits in normal flow directly below the
  // page's main header (occupying the ~85px the section grid reserves via its height calc).
  // Other layouts keep the floating action cluster pinned to the top-right.
  const containerStyle = isTabletView
    ? {
        width: '100%',
        position: 'relative',
        zIndex: 5,
        boxShadow:
          'rgba(50, 50, 93, 0.15) 0px 6px 14px -6px, rgba(0, 0, 0, 0.12) 0px 3px 8px -6px',
        background: '#ffffff',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        justifyContent: 'space-between',
        alignItems: 'center',
        pointerEvents: 'all',
        padding: '12px 16px',
        gap: '12px',
        flexShrink: 0,
      }
    : {
        width: 'auto',
        position: 'fixed',
        top: '125px',
        right: '15px', // Moved left to avoid overlapping scrollbar (was -20px)
        zIndex: 1001, // Higher than view toggle (1000) to appear on top
        boxShadow: showPatientDetails
          ? 'rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px'
          : 'unset',
        pointerEvents: !showPatientDetails ? 'none' : 'unset',
        background: showPatientDetails ? 'white' : 'unset',
        justifyContent: showPatientDetails ? 'space-between' : 'flex-end',
        padding: '8px',
      }

  return (
    <>
      <PMPButtonsContainer className="px-4" alwaysInRow style={containerStyle}>
        {isTabletView && (
          <>
            {currentPage > 0 ? (
              <div>
                <PMPButtonForConsultation onClick={previousSection} sx={PREV_NAV_BUTTON_SX}>
                  <ArrowBack sx={{ fontSize: '1.1rem' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {
                      emrSectionsToShowInOrder?.[
                        (currentPageHistory?.[currentPageHistory?.length - 2] || 0) + 1
                      ]?.title
                    }
                  </span>
                </PMPButtonForConsultation>
              </div>
            ) : (
              <div>
                <PMPButtonForConsultation
                  disableRipple
                  sx={{ ...PREV_NAV_BUTTON_SX, opacity: 0, pointerEvents: 'none' }}
                >
                  <ArrowBack sx={{ fontSize: '1.1rem' }} />
                </PMPButtonForConsultation>
              </div>
            )}
          </>
        )}
        {isTabletView && visibleSectionTitles && (
          <div
            style={{
              flex: 1,
              minWidth: 0,
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '15px',
              color: '#1e293b',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {visibleSectionTitles}
          </div>
        )}
        {isTabletView ? (
          <>{renderRightHandSideButtonsForTabletView()}</>
        ) : inChamber ? (
          // In-chamber on mobile the chamber owns these actions via its header ⋮ menu; hide this
          // floating top-right action cluster so it doesn't double up / clutter the small screen.
          null
        ) : (
          <div
            style={{
              background: 'white',
              padding: '8px',
              borderRadius: '8px',
              boxShadow: 'rgb(0, 0, 0) 0px 0px 6px -2px',
              pointerEvents: 'all',
              justifyContent: 'right',
              gap: '8px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {onQuickPrint && (
              <PMPButtonForConsultation
                whitebg
                onClick={onQuickPrint}
                style={{ pointerEvents: 'all', width: 'min-content' }}
              >
                <Tooltip title="Quick Print" placement="left">
                  <Print />
                </Tooltip>
              </PMPButtonForConsultation>
            )}
            <PMPButtonForConsultation
              disabled={prescriptionSaveLoading}
              onClick={savePrescriptionData}
              style={{ pointerEvents: 'all', width: 'min-content' }}
            >
              <Tooltip title="Save Only" placement="left">
                <Save />
              </Tooltip>
              {prescriptionSaveLoading && <Spinner animation="border" variant="light" size="sm" />}
            </PMPButtonForConsultation>
            <ExportTypeSelector
            emailLabel="Send PDF to patient"
              mainButton={({ handleClick, isOpen }) => {
                return (
                  <PMPButtonForConsultation
                    disabled={prescriptionSaveLoading}
                    onClick={handleClick}
                    style={{ pointerEvents: 'all', width: 'min-content' }}
                  >
                    <Tooltip title="Download PDF" placement="left">
                      <Download />
                    </Tooltip>
                    {prescriptionSaveLoading && (
                      <Spinner animation="border" variant="light" size="sm" />
                    )}
                  </PMPButtonForConsultation>
                )
              }}
              handleSelectExportType={handleDownloadPDF}
              hideOptionsId={[EXPORT_TYPES.excel.id]}
            />
            <PMPButtonForConsultation
              whitebg
              onClick={saveAndPrint}
              style={{ pointerEvents: 'all', width: 'min-content' }}
            >
              <Tooltip title="Save and Print" placement="left">
                <PrintOutlined />
              </Tooltip>
            </PMPButtonForConsultation>
            <SettingsMenuButton onEditSections={onEditSections} />
          </div>
        )}
      </PMPButtonsContainer>
    </>
  )
}
